import {
  MutationCache,
  matchQuery,
  type Query,
  QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

declare module "@tanstack/react-query" {
  // biome-ignore lint/style/useConsistentTypeDefinitions: Module augmentation requires interface
  interface Register {
    mutationMeta: {
      invalidates?: QueryKey[] | "all";
      awaits?: QueryKey[];
      errorMessage?: string;
    };
  }
}

const STALE_TIME_MS = 30_000;
const GC_TIME_MS = 300_000;

export type CreateQueryClientOptions = {
  /**
   * Called when a mutation fails. Receives the error and the `errorMessage`
   * from `meta` so the app can show toasts, log, or redirect as needed.
   */
  onMutationError?: (error: Error, errorMessage?: string) => void;
  /**
   * Custom retry predicate for queries. Return `false` to stop retrying.
   * Defaults to retrying once (failureCount < 1).
   */
  shouldRetry?: (failureCount: number, error: Error) => boolean;
  /** Retry delays in milliseconds; omitted uses TanStack Query's default. */
  retryDelay?: (attemptIndex: number, error: Error) => number;
};

/**
 * Creates a `QueryClient` with shared defaults used across all Arcade apps:
 *
 * - Sensible query defaults (`staleTime: 30s`, `gcTime: 5min`, single retry)
 * - Declarative mutation invalidation via `meta.invalidates` / `meta.awaits`
 * - Pluggable error handling via `onMutationError`
 */
export function createQueryClient(
  options?: CreateQueryClientOptions
): QueryClient {
  const mutationCache = new MutationCache({
    onSuccess: (
      _data,
      _variables,
      _context,
      mutation
    ): void | Promise<void> => {
      const { invalidates, awaits } = mutation.meta || {};

      if (invalidates === "all") {
        queryClient.invalidateQueries();
      } else if (invalidates) {
        queryClient.invalidateQueries({
          predicate: (query: Query): boolean =>
            invalidates.some((queryKey) => matchQuery({ queryKey }, query)),
        });
      }

      if (awaits) {
        return Promise.all(
          awaits.map(
            (queryKey): Promise<void> =>
              queryClient.invalidateQueries(
                { queryKey },
                { cancelRefetch: false }
              )
          )
        ).then(() => {
          return;
        });
      }

      return;
    },
    onError: (error, _variables, _context, mutation) => {
      options?.onMutationError?.(error, mutation.meta?.errorMessage);
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        gcTime: GC_TIME_MS,
        refetchOnWindowFocus: true,
        retry:
          options?.shouldRetry ?? ((failureCount: number) => failureCount < 1),
        retryDelay: options?.retryDelay,
      },
    },
    mutationCache,
  });

  return queryClient;
}
